import { FormEvent, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import TopicCard from "@/components/feature/TopicCard";
import TopicsSidebar from "@/pages/topics/components/TopicsSidebar";
import { topics } from "@/mocks/topics";
import { boards } from "@/mocks/boards";
import { getBoardName } from "@/utils/content";
import { SITE_URL } from "@/utils/site";

type SortKey = "new" | "hot";

export default function Topics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeBoard = searchParams.get("board") ?? "";
  const activeQuery = searchParams.get("q") ?? "";
  const [keyword, setKeyword] = useState(activeQuery);
  const [sort, setSort] = useState<SortKey>("new");

  const filtered = useMemo(() => {
    const query = activeQuery.trim().toLowerCase();
    const list = topics.filter((topic) => {
      const matchBoard = !activeBoard || topic.board === activeBoard;
      const haystack = `${topic.title}${topic.excerpt}${topic.boardName}${topic.tags.join("")}`.toLowerCase();
      const matchQuery = !query || haystack.includes(query);
      return matchBoard && matchQuery;
    });
    if (sort === "hot") {
      return [...list].sort((a, b) => b.views - a.views);
    }
    return list;
  }, [activeBoard, activeQuery, sort]);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next = new URLSearchParams();
    if (activeBoard) next.set("board", activeBoard);
    const value = keyword.trim();
    if (value) next.set("q", value);
    setSearchParams(next);
  };

  const resetFilters = () => {
    setKeyword("");
    setSearchParams(new URLSearchParams());
  };

  const crumbItems = [
    { label: "首页", to: "/" },
    { label: "讨论版", to: activeBoard ? "/topics" : undefined },
    ...(activeBoard ? [{ label: getBoardName(activeBoard) }] : []),
  ];

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: activeBoard ? `${getBoardName(activeBoard)} - 瓜田社区讨论版` : "瓜田社区讨论版",
      url: `${SITE_URL}/topics`,
      inLanguage: "zh-CN",
      description:
        "瓜田社区讨论版汇集今日话题与热帖，支持按板块筛选、关键词搜索，发帖互动与回帖讨论都在这里发生。",
      mainEntity: {
        "@type": "ItemList",
        itemListElement: filtered.slice(0, 10).map((topic, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: topic.title,
          url: `${SITE_URL}/topic/${topic.id}`,
        })),
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "讨论版", item: `${SITE_URL}/topics` },
      ],
    },
  ];

  return (
    <>
      <Seo
        title={
          activeBoard
            ? `${getBoardName(activeBoard)} - 瓜田社区讨论版 | 热帖话题与回帖讨论`
            : "瓜田社区讨论版 | 今日话题与热帖推荐，发帖互动回帖讨论"
        }
        description="进入瓜田社区讨论版，按板块浏览今日话题与热帖推荐，支持关键词搜索、发帖互动与回帖讨论，实时更新讨论版精华内容，找到同好一起聊。"
        keywords="瓜田社区讨论版,瓜田社区今日话题,瓜田社区热帖推荐,瓜田社区发帖互动,瓜田社区回帖讨论,瓜田社区大家聊"
        path="/topics"
        jsonLd={jsonLd}
      />

      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1240px] px-4 py-8 md:px-6 md:py-10">
          <Breadcrumbs items={crumbItems} />
          <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-foreground-950 md:text-3xl">
                {activeBoard ? getBoardName(activeBoard) : "瓜田社区讨论版"}
              </h1>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
                {activeBoard
                  ? boards.find((board) => board.id === activeBoard)?.desc ??
                    "挑一个感兴趣的话题点进去，回帖也可以只写一句话。"
                  : "这里汇集全部讨论帖，按板块筛选或直接搜索关键词，找到你关心的今日话题与热帖。"}
              </p>
            </div>
            <Link
              to="/"
              className="flex h-10 items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              <i className="ri-home-4-line" />
              回首页
            </Link>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1240px] px-4 py-8 md:px-6 md:py-10">
        <form
          onSubmit={handleSearch}
          className="flex w-full items-center gap-2 rounded-lg border border-background-300 bg-background-50 p-2"
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center text-lg text-foreground-500">
            <i className="ri-search-line" />
          </span>
          <input
            type="text"
            name="q"
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            placeholder="搜索话题标题、板块或关键词"
            aria-label="搜索讨论版话题"
            className="min-w-0 flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
          />
          {keyword && (
            <button
              type="button"
              onClick={() => setKeyword("")}
              aria-label="清空搜索"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-foreground-400 transition-colors hover:bg-background-100 hover:text-foreground-700"
            >
              <i className="ri-close-line" />
            </button>
          )}
          <button
            type="submit"
            className="flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            搜索
          </button>
        </form>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-1.5">
            <Link
              to="/topics"
              className={`flex h-8 items-center whitespace-nowrap rounded-full px-3.5 text-sm transition-colors ${
                activeBoard === ""
                  ? "bg-primary-500 text-background-50"
                  : "border border-background-300 text-foreground-600 hover:border-primary-300 hover:text-primary-700"
              }`}
            >
              全部
            </Link>
            {boards.map((board) => (
              <Link
                key={board.id}
                to={`/topics?board=${board.id}`}
                className={`flex h-8 items-center gap-1 whitespace-nowrap rounded-full px-3.5 text-sm transition-colors ${
                  activeBoard === board.id
                    ? "bg-primary-500 text-background-50"
                    : "border border-background-300 text-foreground-600 hover:border-primary-300 hover:text-primary-700"
                }`}
              >
                <i className={board.icon} />
                {board.name}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
            {(
              [
                { key: "new", label: "最新发布", icon: "ri-time-line" },
                { key: "hot", label: "最热讨论", icon: "ri-fire-line" },
              ] as { key: SortKey; label: string; icon: string }[]
            ).map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setSort(item.key)}
                className={`flex h-7 items-center gap-1 whitespace-nowrap rounded-full px-3 text-xs font-medium transition-colors ${
                  sort === item.key
                    ? "bg-background-50 text-foreground-950"
                    : "text-foreground-500 hover:text-foreground-800"
                }`}
              >
                <i className={item.icon} />
                {item.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-7 grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="lg:col-span-8 xl:col-span-9">
            <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-foreground-500">
              <span>
                共找到 <strong className="font-semibold text-primary-700">{filtered.length}</strong>{" "}
                条讨论
              </span>
              {activeQuery && (
                <span className="flex items-center gap-1 rounded-md bg-secondary-100 px-2 py-0.5 text-xs text-secondary-800">
                  关键词：{activeQuery}
                </span>
              )}
            </div>

            {filtered.length > 0 ? (
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                {filtered.map((topic) => (
                  <TopicCard
                    key={topic.id}
                    topic={{
                      ...topic,
                      accent: boards.find((board) => board.id === topic.board)?.accent ?? "primary",
                    }}
                  />
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-2xl text-foreground-400">
                  <i className="ri-search-eye-line" />
                </span>
                <h3 className="mt-4 text-base font-semibold text-foreground-950">
                  没有找到相关的讨论
                </h3>
                <p className="mt-2 text-sm text-foreground-600">
                  换个关键词试试，或者清空筛选看看全部话题。
                </p>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="mt-5 inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-refresh-line" />
                  清空筛选
                </button>
              </div>
            )}
          </div>

          <div className="lg:col-span-4 xl:col-span-3">
            <TopicsSidebar activeBoard={activeBoard} />
          </div>
        </div>
      </div>
    </>
  );
}